import { useEffect, useCallback, useRef, useState } from "react";

import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import { Image } from "@/libraries/design/components/image/src/image";
import { wacToken } from "@/libraries/design/foundations";

import {
    fetchReadmeHtml,
    README_SCOPE_CLASS,
    SYNTAX_STYLES
} from "./integrations-search-readme-utils";

import type { PipeItem } from "./integrations-search-types";

// ============================================================================
// Colors
// ============================================================================

const TEXT_PRIMARY = wacToken("color.extended.blue1000");
const TEXT_SECONDARY = wacToken("color.neutral.light800");
const TEXT_LINK = wacToken("color.primary.blue");
const BG_CODE = wacToken("color.neutral.light100");
const BORDER_COLOR = wacToken("color.neutral.light300");

// ============================================================================
// Styles
// ============================================================================

// Fix 1 & 4: Restructured overlay into wrapper + backdrop button
// Wrapper is purely for positioning (no click handler, no background)
const wrapperStyle = css({
    position: "fixed",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: token("space.300"),
    boxSizing: "border-box"
});

// Backdrop is a proper <button> — fixes a11y errors
// Uses wacToken + opacity instead of hard-coded rgba()
const backdropStyle = css({
    position: "absolute",
    inset: 0,
    backgroundColor: wacToken("color.neutral.dark900"),
    opacity: 0.5,
    border: "none",
    padding: 0,
    cursor: "default"
});

// Fix 2: Use ADS elevation token instead of hard-coded boxShadow
const modalOuterStyle = css({
    position: "relative",
    backgroundColor: wacToken("color.neutral.light0"),
    borderRadius: "8px",
    maxWidth: "720px",
    width: "100%",
    maxHeight: "80vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: token("elevation.shadow.overlay"),
    zIndex: 1
});

const modalInnerStyle = css({
    padding: token("space.400"),
    overflowY: "auto",
    flex: 1
});

const closeButtonStyle = css({
    "position": "absolute",
    "insetBlockStart": token("space.200"),
    "insetInlineEnd": token("space.200"),
    "background": "none",
    "border": "none",
    "fontSize": "24px",
    "cursor": "pointer",
    "color": TEXT_SECONDARY,
    "lineHeight": 1,
    "padding": token("space.100"),
    "borderRadius": "4px",
    "zIndex": 1,

    ":hover": {
        color: TEXT_PRIMARY,
        backgroundColor: BG_CODE
    }
});

const logoAreaStyle = css({
    marginBlockEnd: token("space.200"),
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
});

const logoImgStyle = css({
    maxWidth: 80,
    maxHeight: 80,
    objectFit: "contain"
});

const titleStyle = css({
    margin: `0 0 ${token("space.100")}`,
    fontFamily: "Charlie Text",
    fontSize: 20,
    fontWeight: 600,
    color: TEXT_PRIMARY
});

const descriptionStyle = css({
    margin: `0 0 ${token("space.200")}`,
    fontFamily: "Charlie Text",
    fontSize: 14,
    lineHeight: 1.5,
    color: TEXT_PRIMARY
});

const versionStyle = css({
    margin: `0 0 ${token("space.300")}`,
    fontFamily: "Charlie Text",
    fontSize: 13,
    color: TEXT_SECONDARY
});

const yamlSectionStyle = css({
    marginBlockEnd: token("space.300")
});

const yamlHeadingStyle = css({
    fontSize: 16,
    fontWeight: 600,
    fontFamily: "Charlie Text",
    color: TEXT_PRIMARY,
    margin: `0 0 ${token("space.100")}`
});

const yamlCodeBlockStyle = css({
    backgroundColor: BG_CODE,
    borderRadius: "4px",
    padding: token("space.200"),
    overflowX: "auto",
    fontFamily:
        "SFMono-Medium, 'SF Mono', 'Segoe UI Mono', 'Roboto Mono', 'Ubuntu Mono', Menlo, Consolas, Courier, monospace",
    fontSize: 13,
    lineHeight: 1.5,
    color: TEXT_PRIMARY,
    whiteSpace: "pre",
    margin: 0
});

// Fix 3: Container-level styles ONLY — no nested selectors.
// All child element styles moved to MARKDOWN_CONTENT_STYLES below.
const markdownContainerStyle = css({
    fontFamily: "Charlie Text",
    fontSize: 14,
    lineHeight: 1.6,
    color: TEXT_PRIMARY
});

const readmeLoadingStyle = css({
    textAlign: "center",
    padding: token("space.300"),
    fontSize: 14,
    color: TEXT_SECONDARY
});

const buttonsAreaStyle = css({
    padding: `${token("space.200")} ${token("space.400")}`,
    borderBlockStart: `1px solid ${BORDER_COLOR}`,
    display: "flex",
    justifyContent: "flex-start"
});

const visitRepoButtonStyle = css({
    "display": "inline-block",
    "padding": `${token("space.100")} ${token("space.200")}`,
    "backgroundColor": TEXT_LINK,
    "color": wacToken("color.neutral.light0"),
    "border": "none",
    "borderRadius": "4px",
    "fontFamily": "Charlie Text",
    "fontSize": 14,
    "fontWeight": 600,
    "cursor": "pointer",
    "textDecoration": "none",

    ":hover": {
        opacity: 0.9
    }
});

// ============================================================================
// Scoped styles for README child elements
//
// Injected via a <style> tag so they bypass Compiled's no-nested-selectors
// rule. These target child elements within .bb-readme-content.
// wacToken() and token() return CSS var() expressions, so they work
// fine inside template literal CSS strings.
// ============================================================================

const scope = README_SCOPE_CLASS;

const MARKDOWN_CONTENT_STYLES = `
  .${scope} h1 {
    font-size: 18px;
    font-weight: 600;
    color: ${TEXT_PRIMARY};
    margin: ${token("space.300")} 0 ${token("space.150")};
  }

  .${scope} h2 {
    font-size: 16px;
    font-weight: 600;
    color: ${TEXT_PRIMARY};
    margin: ${token("space.250")} 0 ${token("space.100")};
  }

  .${scope} h3 {
    font-size: 14px;
    font-weight: 600;
    color: ${TEXT_PRIMARY};
    margin: ${token("space.200")} 0 ${token("space.100")};
  }

  .${scope} p {
    color: ${TEXT_PRIMARY};
    margin: 0 0 ${token("space.150")};
  }

  .${scope} ul,
  .${scope} ol {
    color: ${TEXT_PRIMARY};
    margin: 0 0 ${token("space.150")};
    padding-inline-start: ${token("space.300")};
  }

  .${scope} li {
    color: ${TEXT_PRIMARY};
    margin-block-end: ${token("space.050")};
  }

  .${scope} pre {
    background-color: ${BG_CODE};
    border-radius: 4px;
    padding: ${token("space.200")};
    overflow-x: auto;
    margin: 0 0 ${token("space.150")};
    border: 1px solid ${BORDER_COLOR};
  }

  .${scope} code {
    font-family: SFMono-Medium, 'SF Mono', 'Segoe UI Mono', 'Roboto Mono', 'Ubuntu Mono', Menlo, Consolas, Courier, monospace;
    font-size: 13px;
    color: ${TEXT_PRIMARY};
  }

  .${scope} p code,
  .${scope} li code,
  .${scope} td code {
    background-color: ${BG_CODE};
    border-radius: 3px;
    padding: 2px 6px;
    font-size: 12px;
  }

  .${scope} table {
    width: 100%;
    border-collapse: collapse;
    margin: 0 0 ${token("space.150")};
    font-size: 13px;
    color: ${TEXT_PRIMARY};
  }

  .${scope} th,
  .${scope} td {
    border: 1px solid ${BORDER_COLOR};
    padding: ${token("space.100")} ${token("space.150")};
    text-align: left;
    color: ${TEXT_PRIMARY};
  }

  .${scope} th {
    background-color: ${BG_CODE};
    font-weight: 600;
  }

  .${scope} a {
    color: ${TEXT_LINK};
    text-decoration: none;
  }

  .${scope} a:hover {
    text-decoration: underline;
  }

  .${scope} strong {
    color: ${TEXT_PRIMARY};
    font-weight: 600;
  }

  .${scope} em {
    color: ${TEXT_PRIMARY};
  }
`;

// Combine syntax highlighting + markdown content styles
const ALL_README_STYLES = SYNTAX_STYLES + MARKDOWN_CONTENT_STYLES;

// ============================================================================
// Component
// ============================================================================

export interface IntegrationsSearchModalProps {
    item: PipeItem;
    onClose: () => void;
}

export const IntegrationsSearchModal = ({
    item,
    onClose
}: IntegrationsSearchModalProps) => {
    const modalRef = useRef<HTMLDivElement>(null);
    const [readmeHtml, setReadmeHtml] = useState<string | null>(null);
    const [readmeLoading, setReadmeLoading] = useState(true);

    // Fetch README when modal opens
    useEffect(() => {
        let cancelled = false;

        const loadReadme = async () => {
            setReadmeLoading(true);
            const html = await fetchReadmeHtml(item.repositoryPath);
            if (!cancelled) {
                setReadmeHtml(html);
                setReadmeLoading(false);
            }
        };

        loadReadme();

        return () => {
            cancelled = true;
        };
    }, [item.repositoryPath]);

    // Close on Escape
    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                onClose();
            }
        },
        [onClose]
    );

    useEffect(() => {
        document.addEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "hidden";
        modalRef.current?.focus();

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.body.style.overflow = "";
        };
    }, [handleKeyDown]);

    const repoUrl = `https://bitbucket.org/${item.repositoryPath}`;

    return (
        // Wrapper: fixed positioning only, no click handler
        <div css={wrapperStyle}>
            {/* Backdrop: proper <button> for a11y, click-to-close */}
            <button
                type="button"
                css={backdropStyle}
                onClick={onClose}
                aria-label="Close dialog"
                tabIndex={-1}
            />

            {/* Modal */}
            <div
                css={modalOuterStyle}
                role="dialog"
                aria-modal="true"
                aria-label={item.name}
            >
                <button
                    type="button"
                    css={closeButtonStyle}
                    onClick={onClose}
                    aria-label="Close"
                >
                    ×
                </button>

                <div css={modalInnerStyle} ref={modalRef} tabIndex={-1}>
                    <div css={logoAreaStyle}>
                        {/* Fix 5: WAC Image component instead of <img> */}
                        <Image
                            css={logoImgStyle}
                            src={item.logo}
                            alt={item.name}
                        />
                    </div>

                    <h4 css={titleStyle}>{item.name}</h4>

                    <p css={descriptionStyle}>{item.description}</p>

                    <div css={versionStyle}>{item.version}</div>

                    {readmeLoading ? (
                        <div css={readmeLoadingStyle}>
                            Loading documentation…
                        </div>
                    ) : readmeHtml ? (
                        <>
                            <style
                                dangerouslySetInnerHTML={{
                                    __html: ALL_README_STYLES
                                }}
                            />
                            <div
                                className={README_SCOPE_CLASS}
                                css={markdownContainerStyle}
                                dangerouslySetInnerHTML={{
                                    __html: readmeHtml
                                }}
                            />
                        </>
                    ) : (
                        <>
                            {item.yml && (
                                <div css={yamlSectionStyle}>
                                    <h2 css={yamlHeadingStyle}>
                                        YAML Definition
                                    </h2>
                                    <pre css={yamlCodeBlockStyle}>
                                        {item.yml}
                                    </pre>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div css={buttonsAreaStyle}>
                    <a
                        css={visitRepoButtonStyle}
                        href={repoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Visit Repo
                    </a>
                </div>
            </div>
        </div>
    );
};
